import * as Api from '@ViewModels';
import { useMemo } from 'react';
import { useAutomationTemplateQuery } from '../../../../queries/Automation/queries/useAutomationTemplateQuery';

/**
 * Options for configuring the useAutomationFlowTemplate hook.
 */
interface UseAutomationFlowTemplateOptions {
	/** Automation template ID to fetch */
	id: string | undefined;
	/** Whether the query should run (default: true) */
	enabled?: boolean;
	/** Optional impersonation context for admin operations */
	impersonationContext?: Api.IImpersonationContext;
}

/**
 * Return value from useAutomationFlowTemplate with computed template properties.
 */
interface UseAutomationFlowTemplateReturn {
	/** The full automation template from the API */
	template: Api.IAutomationTemplate | undefined;
	/** Whether the template is currently being fetched */
	isLoading: boolean;
	/** Whether an error occurred during fetch */
	isError: boolean;
	/** The error object if fetch failed */
	error: Error | null;
	/** Whether the template has unpublished draft changes */
	hasDraft: boolean;
	/** Whether the template has been published at least once */
	isPublished: boolean;
	/** Whether the template is currently active (status === Published) */
	isActive: boolean;
	/** Array of steps to display (prefers draft steps over published) */
	steps: Api.IAutomationStep[];
	/** Trigger configuration to display (prefers draft trigger over published) */
	trigger: Api.IAutomationTrigger | undefined;
	/** Function to manually refetch the template data */
	refetch: () => void;
}

/**
 * Fetches an automation template and provides computed properties for the flow editor.
 *
 * This hook is the primary data source for the automation flow editor. It wraps
 * useAutomationTemplateQuery and adds computed properties to simplify working with
 * draft vs published versions of the automation.
 *
 * Key behaviors:
 * - Automatically prefers draft versions over published when both exist
 * - Computes status flags (hasDraft, isPublished, isActive) from template state
 * - Provides unified steps/trigger arrays regardless of draft status
 * - Handles loading and error states from the underlying query
 *
 * Draft vs Published:
 * - draftSteps/draftTrigger: Current working version, may have unpublished changes
 * - publishedSteps/publishedTrigger: Last committed version, what contacts see
 * - When editing, always work with the draft version
 * - When viewing active automations, show the published version
 *
 * @param options - Configuration object
 * @param options.id - Automation template ID to fetch
 * @param options.enabled - Whether to enable the query (default: true)
 * @param options.impersonationContext - Optional impersonation context for admin users
 * @returns Object containing template data and computed properties
 *
 * @example
 * ```typescript
 * // In the automation editor:
 * const { template, steps, trigger, hasDraft, isLoading } = useAutomationFlowTemplate({
 *   id: templateId,
 *   enabled: Boolean(templateId)
 * });
 *
 * // steps will be draftSteps if they exist, otherwise publishedSteps
 * if (isLoading) return <LoadingSpinner />;
 *
 * // Check if there are unsaved changes:
 * if (hasDraft) {
 *   showSaveIndicator();
 * }
 * ```
 */
export function useAutomationFlowTemplate({
	id,
	enabled = true,
	impersonationContext,
}: UseAutomationFlowTemplateOptions): UseAutomationFlowTemplateReturn {
	const {
		data: template,
		isLoading,
		isError,
		error,
		refetch,
	} = useAutomationTemplateQuery({
		id: id || '',
		enabled: enabled && Boolean(id),
		impersonationContext,
	});

	const hasDraft = useMemo(() => {
		return Boolean(template?.draft?.steps && template.draft.steps.length > 0);
	}, [template]);

	const isPublished = useMemo(() => {
		return Boolean(template?.published?.steps && template.published.steps.length > 0);
	}, [template]);

	const isActive = useMemo(() => {
		return template?.status === Api.TemplateStatus.Published;
	}, [template]);

	const steps = useMemo(() => {
		if (!template) {
			return [];
		}
		return template.draft?.steps || template.published?.steps || [];
	}, [template]);

	const trigger = useMemo(() => {
		if (!template) {
			return undefined;
		}
		return template.draft?.trigger || template.published?.trigger;
	}, [template]);

	return {
		template,
		isLoading,
		isError,
		error,
		hasDraft,
		isPublished,
		isActive,
		steps,
		trigger,
		refetch,
	};
}
